import { useCallback, useMemo } from 'react';

import { TFunction } from 'i18next';
import sortBy from 'lodash-es/sortBy';
import { CheckIcon, XIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import CardBase from '@/components/organisms/Integrations/Components/CardBase';
import { Badge } from '@/components/ui/badge';
import { PREVENT_AUTOFILL_PROPS } from '@/components/ui/constants';
import { Autocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Spinner } from '@/components/ui/spinner';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { useTwilioVoice } from '@/hooks/useTwilioVoice';
import {
  IntegrationStatus,
  TwilioVoiceIntegration,
  VoiceCredentialStatus,
} from '@/shared/types/integration';
import { formatPhoneNumber, getFlagByPhoneNumber } from '@/util/util';

import {
  getMoveoIntegrationId,
  getRegionBadgeLabel,
  isTwilioDefaultUrl,
  parseTwilioRegion,
} from '../utils';

type UnavailableReason = 'wrong_region' | 'in_use';

const toPhoneLabel = (phoneNumber: string) => {
  const flag = getFlagByPhoneNumber(phoneNumber);
  const formatted = formatPhoneNumber(phoneNumber);
  return flag ? `${flag} ${formatted}` : formatted;
};

const getUnavailableTooltip = (
  t: TFunction,
  reason: UnavailableReason,
  owner: string | undefined
) => {
  if (reason === 'wrong_region') {
    return t('integrations.voice_common.phone_number_unavailable_region');
  }
  if (owner) {
    return t('integrations.voice_common.phone_number_unavailable_in_use_by', { 0: owner });
  }
  return t('integrations.voice_common.phone_number_unavailable_in_use');
};

/**
 * Account configuration card for Twilio Voice integration
 *
 * Provides form fields for:
 * - Account SID (required)
 * - Auth Token (required)
 * - Phone Number (required)
 * - Welcome Trigger Message (switch: on = 'defaultstartmessage', off = '')
 *
 * Uses the form context from the parent Integration page
 */
const Account = ({ integration }: { integration: TwilioVoiceIntegration }) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TwilioVoiceIntegration>();

  // Account SID can't be updated after save — integrations-controller ignores the change
  const isAccountSidSaved = !!integration.config.account_sid;
  const { phones, verificationStatus, accountSidStatus, authTokenStatus } = useTwilioVoice(
    integration.integration_id
  );

  // Get region display from Twilio API URL (full name for Auth Token badge)
  const regionDisplay = useMemo(() => {
    const parsed = parseTwilioRegion(window.TWILIO_API_URL);
    if (!parsed) {
      return null;
    }
    const regionName = t(`integrations.twilio_voice.region_${parsed.region}`);
    return `${regionName} (${parsed.region.toUpperCase()})`;
  }, [t]);

  const selectedPhone = useWatch({ control, name: 'config.phone_number' });
  const deskId = useDeskId();
  const { integrations } = useIntegrations(deskId);

  // An integration wired before this picker existed stores no phone_number of its own.
  const connectedPhone = phones?.find(
    (phone) =>
      (!!integration.config.application_sid &&
        phone.voice_application_sid === integration.config.application_sid) ||
      getMoveoIntegrationId(phone.voice_url) === integration.integration_id
  )?.phone_number;

  // window.TWILIO_API_URL is a build-time constant injected via server config.js
  const deploymentRegion = useMemo(() => {
    const parsed = parseTwilioRegion(window.TWILIO_API_URL);
    return parsed?.region ?? 'us1';
  }, []);

  type PhoneOptionData = { voiceRegion?: string; unavailableTooltip?: string };
  type UnavailablePhone = NonNullable<typeof phones>[number] & {
    unavailableReason: UnavailableReason;
  };

  const phoneOptions = useMemo(() => {
    const availablePhones: typeof phones = [];
    const unavailablePhones: UnavailablePhone[] = [];

    phones?.forEach((phone) => {
      const isCurrentIntegrationPhone =
        phone.phone_number === selectedPhone || phone.phone_number === connectedPhone;

      // Currently selected phone is always available
      if (isCurrentIntegrationPhone) {
        availablePhones.push(phone);
        return;
      }

      // Available only if: no active webhook AND routed to this region (or region unknown)
      const isUnconfigured = isTwilioDefaultUrl(phone.voice_url) && !phone.voice_application_sid;
      const isThisRegion = !phone.voice_region || phone.voice_region === deploymentRegion;

      if (isUnconfigured && isThisRegion) {
        availablePhones.push(phone);
      } else {
        // Wrong-region wins when both apply — it's the reason we can state with certainty
        // (out-of-region numbers may or may not be in use, we can't tell from here).
        const unavailableReason: UnavailableReason = isThisRegion ? 'in_use' : 'wrong_region';
        unavailablePhones.push({ ...phone, unavailableReason });
      }
    });

    const availableOptions = availablePhones.map((phone) => {
      const label = toPhoneLabel(phone.phone_number);
      return {
        value: phone.phone_number,
        label,
        category: t('common.available'),
        data: { voiceRegion: phone.voice_region } as PhoneOptionData,
      };
    });

    const unavailableOptions = unavailablePhones.map((phone) => {
      const label = toPhoneLabel(phone.phone_number);
      const ownerId = getMoveoIntegrationId(phone.voice_url);
      const owner = integrations?.find((item) => item.integration_id === ownerId)?.name;
      const unavailableTooltip = getUnavailableTooltip(t, phone.unavailableReason, owner);
      return {
        value: phone.phone_number,
        label,
        disabled: true,
        category:
          phone.unavailableReason === 'wrong_region'
            ? t('integrations.voice_common.phone_number_wrong_region')
            : t('integrations.voice_common.phone_number_in_use'),
        data: { voiceRegion: phone.voice_region, unavailableTooltip } as PhoneOptionData,
      };
    });

    const sortedAvailable = sortBy(availableOptions, [
      (option) => (option.value === selectedPhone ? 0 : 1),
      'label',
    ]);
    const sortedUnavailable = sortBy(unavailableOptions, ['label']);

    return [
      { label: t('common.none'), value: '', data: {} as PhoneOptionData },
      ...sortedAvailable,
      ...sortedUnavailable,
    ];
  }, [phones, selectedPhone, connectedPhone, t, deploymentRegion, integrations]);

  const renderPhoneValue = (option: { label: string; value: string }) =>
    option.value ? option.label : t('common.select_dots');

  const renderPhoneOption = useCallback((option: { label: string; data?: PhoneOptionData }) => {
    const regionLabel = getRegionBadgeLabel(option.data?.voiceRegion);
    const tooltip = option.data?.unavailableTooltip;
    const row = (
      <span className="flex w-full items-center justify-between gap-2">
        <span className="truncate">{option.label}</span>
        {regionLabel && (
          <Badge type="secondary" flavour="neutral" size="xs" rounded={false}>
            {regionLabel}
          </Badge>
        )}
      </span>
    );

    if (!tooltip) {
      return row;
    }

    // Disabled cmdk items set pointer-events:none, which suppresses hover —
    // re-enable on the wrapper so the tooltip fires (the row stays unselectable
    // because Autocomplete gates onSelect on `disabled`).
    return (
      <InfoTooltip content={tooltip} size="large" side="right">
        <span className="pointer-events-auto block w-full">{row}</span>
      </InfoTooltip>
    );
  }, []);

  // Separate validation indicators for each field
  const AccountSidValidationAddon = useCallback(() => {
    if (accountSidStatus === VoiceCredentialStatus.VERIFIED) {
      return <CheckIcon className="size-4 text-success" />;
    } else if (accountSidStatus === VoiceCredentialStatus.INVALID) {
      return <XIcon className="size-4 text-destructive" />;
    }
    return null;
  }, [accountSidStatus]);

  const AuthTokenValidationAddon = useCallback(() => {
    if (authTokenStatus === VoiceCredentialStatus.VALIDATING) {
      return <Spinner aria-label={t('common.validating_auth_token')} />;
    } else if (authTokenStatus === VoiceCredentialStatus.VERIFIED) {
      return <CheckIcon className="size-4 text-success" />;
    } else if (authTokenStatus === VoiceCredentialStatus.INVALID) {
      return <XIcon className="size-4 text-destructive" />;
    }
    return null;
  }, [authTokenStatus, t]);

  return (
    <CardBase
      title={t('common.account')}
      status={
        verificationStatus === VoiceCredentialStatus.VERIFIED
          ? IntegrationStatus.VERIFIED
          : integration.status
      }
    >
      <FormField
        control={control}
        name="config.account_sid"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.twilio_voice_connect.account_sid.title')}</FormLabel>
            <FormControl>
              <InputGroup>
                <InputGroupInput
                  {...field}
                  {...PREVENT_AUTOFILL_PROPS}
                  value={field.value || ''}
                  type="text"
                  disabled={isAccountSidSaved}
                  placeholder={t('integrations.twilio_voice_connect.account_sid.placeholder')}
                  onChange={(e) => field.onChange(e.target.value.trim())}
                />
                {!isAccountSidSaved && (
                  <InputGroupAddon align="inline-end">
                    <AccountSidValidationAddon />
                  </InputGroupAddon>
                )}
              </InputGroup>
            </FormControl>
            <FormDescription>
              {isAccountSidSaved
                ? t('integrations.twilio_voice_connect.account_sid.saved_description')
                : t('integrations.twilio_voice_connect.account_sid.description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="config.auth_token"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel>{t('integrations.twilio_voice_connect.auth_token.title')}</FormLabel>
              {regionDisplay && (
                <InfoTooltip content={t('integrations.twilio_voice.region_tooltip')} size="large">
                  <div className="inline-flex">
                    <Badge type="secondary" flavour="informational" rounded={false}>
                      {regionDisplay}
                    </Badge>
                  </div>
                </InfoTooltip>
              )}
            </div>
            <FormControl>
              <InputGroup>
                <InputGroupInput
                  {...field}
                  {...PREVENT_AUTOFILL_PROPS}
                  value={field.value || ''}
                  type="password"
                  placeholder={t('integrations.twilio_voice_connect.auth_token.placeholder')}
                  onChange={(e) => field.onChange(e.target.value.trim())}
                />
                <InputGroupAddon align="inline-end">
                  <AuthTokenValidationAddon />
                </InputGroupAddon>
              </InputGroup>
            </FormControl>
            <FormDescription>
              {t('integrations.twilio_voice_connect.auth_token.description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {verificationStatus === VoiceCredentialStatus.VERIFIED && (
        <FormField
          control={control}
          name="config.phone_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('field.phone_number')}</FormLabel>
              <FormControl>
                <Autocomplete
                  options={phoneOptions}
                  value={field.value}
                  onChange={field.onChange}
                  renderOption={renderPhoneOption}
                  renderValue={renderPhoneValue}
                  searchValues
                />
              </FormControl>
              <FormDescription>
                {t('integrations.voice_common.phone_number_description')}
                {!!connectedPhone &&
                  ` ${t('integrations.voice_common.phone_number_connected', {
                    0: formatPhoneNumber(connectedPhone),
                  })}`}
              </FormDescription>
            </FormItem>
          )}
        />
      )}
    </CardBase>
  );
};

export default Account;
